import { useCallback } from "react";
import { useAuth } from "../helper";
import { httpService } from "../../services/httpService";
import { API_CONFIG } from "../../shared/constants";

export type AnalyticsEventName =
  | "page_viewed"
  | "button_clicked"
  | "modal_opened"
  | "modal_closed"
  | "tab_switched"
  | "filter_applied"
  | "search_performed"
  | "export_triggered"
  | "form_submitted"
  | "form_abandoned"
  | "error_displayed"
  | "upgrade_prompt_shown";

export type AnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

interface TrackEventPayload {
  event_name: AnalyticsEventName;
  properties: AnalyticsProperties;
}

export const useTrackEvent = () => {
  const { user } = useAuth();

  const trackEvent = useCallback(
    (eventName: AnalyticsEventName, properties: AnalyticsProperties = {}): void => {
      const enriched: AnalyticsProperties = {
        page_path: window.location.pathname,
        user_plan: user?.plan?.name ?? null,
        subscription_status: user?.subscription_status ?? null,
        ...properties,
      };

      const payload: TrackEventPayload = {
        event_name: eventName,
        properties: enriched,
      };

      // Skip if not authenticated — avoids triggering the session-expired modal
      // on public/auth pages where no JWT token exists yet.
      if (!httpService.getAccessToken()) return;

      // Fire-and-forget — intentionally not awaited
      httpService
        .post<{ status: string }>(API_CONFIG.path.analyticsTrack, payload)
        .catch(() => {
          // Silently absorb — analytics must never break the UI
        });
    },
    [user?.plan?.name, user?.subscription_status],
  );

  return { trackEvent };
};
